import { useEffect } from 'react';
import { useDisplayPreferences } from '../../context/DisplayPreferencesContext';

/**
 * ThemeManager Component
 * Applies display preferences to the DOM (theme, font size, compact mode)
 */
export default function ThemeManager() {
  const { preferences } = useDisplayPreferences();

  // Apply theme to document
  useEffect(() => {
    const applyTheme = (theme) => {
      const root = document.documentElement;
      
      if (theme === 'dark') {
        root.classList.add('dark');
      } else if (theme === 'light') {
        root.classList.remove('dark');
      } else {
        // System preference
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      }
    };

    applyTheme(preferences.theme);

    // Listen for system theme changes if theme is 'system'
    if (preferences.theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        if (e.matches) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [preferences.theme]);

  // Apply font size to body
  useEffect(() => {
    const fontSizeMap = {
      small: '14px',
      medium: '16px',
      large: '18px'
    };
    document.body.style.fontSize = fontSizeMap[preferences.font_size] || '16px';
  }, [preferences.font_size]);

  // Apply compact mode to root container
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      if (preferences.compact_mode) {
        root.classList.add('compact-mode');
        // Add CSS for compact mode
        const style = document.createElement('style');
        style.id = 'compact-mode-styles';
        style.textContent = `
          .compact-mode .space-y-8 { gap: 1rem !important; }
          .compact-mode .space-y-6 { gap: 0.75rem !important; }
          .compact-mode .space-y-4 { gap: 0.5rem !important; }
          .compact-mode .p-6 { padding: 1rem !important; }
          .compact-mode .p-4 { padding: 0.75rem !important; }
          .compact-mode .py-8 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
          .compact-mode .py-6 { padding-top: 0.75rem !important; padding-bottom: 0.75rem !important; }
        `;
        if (!document.getElementById('compact-mode-styles')) {
          document.head.appendChild(style);
        }
      } else {
        root.classList.remove('compact-mode');
        const existingStyle = document.getElementById('compact-mode-styles');
        if (existingStyle) {
          existingStyle.remove();
        }
      }
    }
  }, [preferences.compact_mode]);

  // This component doesn't render anything
  return null;
}
